import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Button, 
  CircularProgress, 
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import { 
  Comment as CommentIcon, 
  Check as CheckIcon,
  ThumbUp as ThumbUpIcon 
} from '@mui/icons-material';
import { baseClient } from '../api/baseClient';

interface Comment {
  id: string;
  section: string;
  comment_text: string;
  author_name: string;
  created_at: string;
}

interface ProposalData {
  id: string;
  deal_name: string;
  content: string;
  status: string;
  created_at: string;
  comments: Comment[];
}

export function PortalProposalPage() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [proposal, setProposal] = useState<ProposalData | null>(null);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState('');
  const [commentText, setCommentText] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Authenticate and fetch proposal data
  useEffect(() => {
    const authenticate = async () => {
      try {
        // First, authenticate with the share token
        const authResponse = await baseClient.post('/portal/authenticate', {
          share_token: shareToken
        });

        // Fetch the proposal data
        const proposalResponse = await baseClient.get(`/portal/proposals/${shareToken}`);
        setProposal(proposalResponse.data);
        
        setLoading(false);
      } catch (err) {
        console.error('Error authenticating or fetching proposal:', err);
        setError('Failed to load proposal. The link may be invalid or expired.');
        setLoading(false);
      }
    };
    
    if (shareToken) {
      authenticate();
    }
  }, [shareToken]);

  const handleCommentSubmit = async () => {
    if (!commentText || !authorName || !selectedSection) return;
    
    setSubmitting(true);
    try {
      await baseClient.post(`/portal/proposals/${shareToken}/comments`, {
        section: selectedSection,
        comment_text: commentText,
        author_name: authorName
      });

      // Refresh proposal data to get the new comment
      const proposalResponse = await baseClient.get(`/portal/proposals/${shareToken}`);
      setProposal(proposalResponse.data);
      
      // Close dialog and reset form
      setCommentDialogOpen(false);
      setCommentText('');
      setSelectedSection('');
    } catch (err) {
      console.error('Error submitting comment:', err);
      alert('Failed to submit comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptProposal = async () => {
    if (!authorName) return;
    
    setSubmitting(true);
    try {
      await crmClient.post(`/portal/proposals/${shareToken}/accept`, {
        name: authorName
      });
      
      // Refresh proposal data
      const proposalResponse = await crmClient.get(`/portal/proposals/${shareToken}`);
      setProposal(proposalResponse.data);
      
      // Close dialog
      setAcceptDialogOpen(false);
      
      // Show success message
      alert('Proposal accepted successfully!');
    } catch (err) {
      console.error('Error accepting proposal:', err);
      alert('Failed to accept proposal. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openCommentDialog = (section: string) => {
    setSelectedSection(section);
    setCommentDialogOpen(true);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading proposal...
        </Typography>
      </Container>
    );
  }

  if (error || !proposal) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Alert severity="error">{error || 'Failed to load proposal'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {proposal.deal_name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Created on {new Date(proposal.created_at).toLocaleDateString()}
        </Typography>
        
        {proposal.status === 'Accepted' ? (
          <Chip 
            icon={<CheckIcon />} 
            label="Accepted" 
            color="success" 
            sx={{ mt: 2 }} 
          />
        ) : (
          <Button
            variant="contained"
            color="success"
            startIcon={<ThumbUpIcon />}
            onClick={() => setAcceptDialogOpen(true)}
            sx={{ mt: 2 }}
          >
            Accept Proposal
          </Button>
        )}
      </Paper>
      
      {/* Proposal Content */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ position: 'relative' }}>
          <div dangerouslySetInnerHTML={{ __html: proposal.content }} />
          
          {/* Comment buttons */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<CommentIcon />}
              onClick={() => openCommentDialog('general')}
            >
              Add Comment
            </Button>
          </Box>
        </Box>
      </Paper>
      
      {/* Comments Section */}
      {proposal.comments && proposal.comments.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Comments
          </Typography>
          <List>
            {proposal.comments.map((comment) => (
              <Box key={comment.id}>
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography component="span" variant="subtitle1">
                          {comment.author_name}
                        </Typography>
                        <Chip 
                          label={comment.section} 
                          size="small" 
                          color="primary" 
                          variant="outlined" 
                        />
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="text.primary">
                          {comment.comment_text}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {new Date(comment.created_at).toLocaleString()}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
                <Divider component="li" />
              </Box>
            ))}
          </List>
        </Paper>
      )}
      
      {/* Comment Dialog */}
      <Dialog open={commentDialogOpen} onClose={() => setCommentDialogOpen(false)}>
        <DialogTitle>Add Comment</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Your Name"
            fullWidth
            variant="outlined"
            value={authorName}
            onChange={(e) => setAuthorName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="comment"
            label="Your Comment"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCommentSubmit} 
            variant="contained"
            disabled={!commentText || !authorName || submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Accept Dialog */}
      <Dialog open={acceptDialogOpen} onClose={() => setAcceptDialogOpen(false)}>
        <DialogTitle>Accept Proposal</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            You are about to accept this proposal. Please confirm your name below.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            id="acceptName"
            label="Your Name"
            fullWidth
            variant="outlined"
            value={authorName}
            onChange={(e) => setAuthorName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcceptDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAcceptProposal} 
            variant="contained"
            color="success"
            disabled={!authorName || submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Accept Proposal'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
} 